import { Check, EnvelopeSimple } from '@phosphor-icons/react'
import { Alert } from 'antd'
import { useCountdown } from 'hooks/useCountdown'
import React from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import TextField from 'ui/molecules/TextField'

import { postForgotPassword } from '../../api/auth'
import Ui from '../../ui'
import { objectHasPropWithValue } from '../../utils/helpers'
import {
  error,
  initial,
  isError,
  isSuccess,
  success,
} from '../../utils/remoteData'
import { emailSchema, schemasValidate } from '../../utils/schemas'
import AuthCard from './components/AuthCard'

const ForgotPasswordSchema = {
  email: emailSchema,
}

function ForgotPassword() {
  const { seconds, resume } = useCountdown({
    initialSeconds: 60,
  })

  const [isLoading, setIsLoading] = React.useState(false)
  const [formValues, setFormValues] = React.useState({
    email: '',
  })
  const [formError, setFormError] = React.useState({
    email: false,
  })
  const [formErrorLabel, setFormErrorLabel] = React.useState({
    email: '',
  })

  const [hasFormError, setHasFormError] = React.useState(false)

  const [forgotPasswordRequest, setForgotPasswordRequest] =
    React.useState(initial())

  React.useEffect(() => {
    setHasFormError(objectHasPropWithValue(formError, true))
  }, [formError])

  const onChange = (name) => (value) => {
    setFormValues((current) => ({ ...current, [name]: value }))
    formError[name] &&
      schemasValidate(
        [[name, value, ForgotPasswordSchema[name]]],
        setFormError,
        setFormErrorLabel
      )
  }

  const onSubmit = async () => {
    const { email } = formValues
    const isValid = await schemasValidate(
      [['email', email, ForgotPasswordSchema.email]],
      setFormError,
      setFormErrorLabel
    )
    if (!isValid) {
      return
    }

    try {
      setIsLoading(true)
      const response = await postForgotPassword({ email })
      setForgotPasswordRequest(success(response))
      resume()
      setIsLoading(false)
    } catch (err) {
      setForgotPasswordRequest(error(err.message))
      toast.error(err.message)
      setIsLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Forgot Password/Zentake</title>
      </Helmet>

      <AuthCard>
        {!isSuccess(forgotPasswordRequest) ? (
          <>
            <h1 className="mb-2 mt-3 text-center text-2xl font-bold text-gray-800">
              Reset Your Password
            </h1>
            <p className="text-center text-sm text-gray-600">
              Just let us know the email you use to sign in and we'll help you
              get your password back
            </p>
            {isError(forgotPasswordRequest) && (
              <div className="mt-2 flex flex-1 justify-center">
                <Alert
                  showIcon
                  className="w-full"
                  message={forgotPasswordRequest.error}
                  type="error"
                ></Alert>
              </div>
            )}
            <div className="mt-3">
              <TextField
                errorLabel={formErrorLabel.email}
                hasError={formError.email}
                label="Email"
                name="email"
                onChange={onChange('email')}
                placeholder="Type your email"
                type="text"
                value={formValues.email}
              />
            </div>
            <div className="mt-3 grid">
              <Ui.PrimaryButton
                disabled={hasFormError}
                loading={isLoading}
                onClick={onSubmit}
              >
                Send Password Reset Email
              </Ui.PrimaryButton>
              <div className="mt-3 flex flex-1 flex-shrink-0 justify-center">
                <Link
                  className="text-primary transition duration-150 ease-in-out hover:text-primary focus:text-primary focus:outline-none"
                  to="/login"
                >
                  ← Take me back to login
                </Link>
              </div>
            </div>
          </>
        ) : null}
        {isSuccess(forgotPasswordRequest) ? (
          <>
            <div className="mx-auto mb-1 mt-2 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <Check className="text-4xl text-green-700" />
            </div>
            <h1 className="my-3 text-center text-2xl font-bold text-gray-800">
              Check Your Email
            </h1>
            <p className="text-center text-base leading-5 text-gray-700">
              We've sent a password reset link to {formValues.email}
            </p>
            <p className="my-4 flex rounded bg-primary/10 p-3 text-center text-sm text-blue-700">
              <EnvelopeSimple className="text-xl" /> Didn't receive the email?
              Check your spam folder or try again.
            </p>
            {seconds <= 0 ? (
              <Ui.PrimaryButton
                className="mx-auto w-[fit-content]"
                disabled={hasFormError}
                loading={isLoading}
                onClick={onSubmit}
              >
                Send New Reset Link
              </Ui.PrimaryButton>
            ) : null}
            {seconds > 0 ? (
              <p className="text-center text-sm text-gray-600">
                You can request another email in{' '}
                <strong>{seconds} seconds</strong>
              </p>
            ) : null}
            <div className="mt-3 flex flex-1 flex-shrink-0 justify-center">
              <Link
                className="text-primary transition duration-150 ease-in-out hover:text-primary focus:text-primary focus:outline-none"
                to="/login"
              >
                ← Take me back to login
              </Link>
            </div>
          </>
        ) : null}
      </AuthCard>
    </>
  )
}

export default ForgotPassword
